import React, { useState } from 'react'

import { useMutation } from '@apollo/client'

import { EmailDeliveryMethod } from '$adminSrc/__generated__/graphql'

import { client } from '../../../../apollo'
import { UPDATE_TENANT_EMAIL_CONFIG } from '../../../../graphql/tenant'

type FormState = {
  id: string
  emailDeliveryMethod: EmailDeliveryMethod
  emailCioAppApiKey: string | null
  emailCioTrackApiKey: string | null
  emailCioTrackSiteId: string | null
  emailCioWebhookSigningKey: string | null
  emailCioReportingWebhookSigningKey: string | null
  emailDeliveryWebhookUrl: string | null
  emailDeliveryWebhookRequestHeaders: string | null
  emailDeliveryWebhookTimeout: number | null
}

export default function UpdateTenantEmailConfigForm() {
  const [formData, setFormData] = useState<FormState>({
    id: '',
    emailDeliveryMethod: EmailDeliveryMethod.Customerio,
    emailCioAppApiKey: undefined,
    emailCioTrackApiKey: undefined,
    emailCioTrackSiteId: undefined,
    emailCioWebhookSigningKey: undefined,
    emailCioReportingWebhookSigningKey: undefined,
    emailDeliveryWebhookUrl: undefined,
    emailDeliveryWebhookRequestHeaders: undefined,
    emailDeliveryWebhookTimeout: undefined,
  })

  function updateFormData(o: Partial<FormState>) {
    setFormData((prev) => ({ ...prev, ...o }))
  }

  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<boolean>(false)

  const [updateTenantEmailConfig, { loading }] = useMutation(
    UPDATE_TENANT_EMAIL_CONFIG,
    {
      onCompleted: (data) => {
        const result = data.adminUpdateTenantEmailConfig
        if (result.statusCode === 200) {
          setSuccess(true)
        } else {
          setError(result.error || 'An unknown error occurred')
        }
      },
      onError: (err) => {
        setError(err.message || 'Failed to create tenant')
      },
      client,
    },
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)

    try {
      const emailConfig = {
        emailCioAppApiKey: formData.emailCioAppApiKey,
        emailCioTrackApiKey: formData.emailCioTrackApiKey,
        emailCioTrackSiteId: formData.emailCioTrackSiteId,
        emailCioWebhookSigningKey: formData.emailCioWebhookSigningKey,
        emailCioReportingWebhookSigningKey:
          formData.emailCioReportingWebhookSigningKey,
        emailDeliveryWebhookUrl: formData.emailDeliveryWebhookUrl,
        emailDeliveryWebhookRequestHeaders:
          formData.emailDeliveryWebhookRequestHeaders,
        emailDeliveryWebhookTimeout: formData.emailDeliveryWebhookTimeout,
      }
      if (formData.emailDeliveryMethod === EmailDeliveryMethod.Blackhole) {
        emailConfig.emailCioAppApiKey = null
        emailConfig.emailCioTrackApiKey = null
        emailConfig.emailCioTrackSiteId = null
        emailConfig.emailCioWebhookSigningKey = null
        emailConfig.emailCioReportingWebhookSigningKey = null
      }

      if (formData.emailDeliveryMethod !== EmailDeliveryMethod.Webhook) {
        emailConfig.emailDeliveryWebhookUrl = null
        emailConfig.emailDeliveryWebhookRequestHeaders = null
        emailConfig.emailDeliveryWebhookTimeout = null
      }

      await updateTenantEmailConfig({
        variables: {
          id: formData.id,
          emailDeliveryMethod: formData.emailDeliveryMethod,
          ...emailConfig,
        },
      })
    } catch (_err: any) {
      // Error handling is done in onError callback
    }
  }

  return (
    <div>
      {success && <div>Tenant updated successfully!</div>}

      {error && <div>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div>
          <label htmlFor="id-x">
            <strong>Tenant ID</strong>
            <br />
            <input
              type="text"
              id="id-x"
              name="id-x"
              value={formData.id}
              onChange={(e) => updateFormData({ id: e.target.value })}
              required
            />
          </label>
        </div>
        <div>
          <strong>Email delivery method</strong>
          <br />
          <label htmlFor="email-delivery-method-customerio-x">
            <input
              type="radio"
              id="email-delivery-method-customerio-x"
              name="email-delivery-method-x"
              value={EmailDeliveryMethod.Customerio}
              checked={
                formData.emailDeliveryMethod === EmailDeliveryMethod.Customerio
              }
              onChange={(e) => {
                if (e.target.checked) {
                  updateFormData({
                    emailDeliveryMethod: EmailDeliveryMethod.Customerio,
                  })
                }
              }}
            />
            Customer.io
          </label>
          <br />
          <label htmlFor="email-delivery-method-webhook-x">
            <input
              type="radio"
              id="email-delivery-method-webhook-x"
              name="email-delivery-method-x"
              value={EmailDeliveryMethod.Webhook}
              checked={
                formData.emailDeliveryMethod === EmailDeliveryMethod.Webhook
              }
              onChange={(e) => {
                if (e.target.checked) {
                  updateFormData({
                    emailDeliveryMethod: EmailDeliveryMethod.Webhook,
                  })
                }
              }}
            />
            Webhook
          </label>
          <br />
          <label htmlFor="email-delivery-method-blackhole-x">
            <input
              type="radio"
              id="email-delivery-method-blackhole-x"
              name="email-delivery-method-x"
              value={EmailDeliveryMethod.Blackhole}
              checked={
                formData.emailDeliveryMethod === EmailDeliveryMethod.Blackhole
              }
              onChange={(e) => {
                if (e.target.checked) {
                  updateFormData({
                    emailDeliveryMethod: EmailDeliveryMethod.Blackhole,
                  })
                }
              }}
            />
            Blackhole
          </label>
        </div>
        {formData.emailDeliveryMethod !== EmailDeliveryMethod.Blackhole && (
          <div>
            <strong>Customer.io Configuration</strong>
            <br />
            <label htmlFor="email-cio-app-api-key-x">
              App API key &nbsp;
              <input
                type="text"
                id="email-cio-app-api-key-x"
                name="email-cio-app-api-key-x"
                value={formData.emailCioAppApiKey}
                onChange={(e) => {
                  updateFormData({ emailCioAppApiKey: e.target.value })
                }}
              />
            </label>
            <br />
            <label htmlFor="email-cio-track-api-key-x">
              Track API key &nbsp;
              <input
                type="text"
                id="email-cio-track-api-key-x"
                name="email-cio-track-api-key-x"
                value={formData.emailCioTrackApiKey}
                onChange={(e) => {
                  updateFormData({ emailCioTrackApiKey: e.target.value })
                }}
              />
            </label>
            <br />
            <label htmlFor="email-cio-track-site-id-x">
              Track site id &nbsp;
              <input
                type="text"
                id="email-cio-track-site-id-x"
                name="email-cio-track-site-id-x"
                value={formData.emailCioTrackSiteId}
                onChange={(e) => {
                  updateFormData({ emailCioTrackSiteId: e.target.value })
                }}
              />
            </label>
            <br />
            <label htmlFor="email-cio-webhook-signing-key-x">
              Webhook signing key &nbsp;
              <input
                type="text"
                id="email-cio-webhook-signing-key-x"
                name="email-cio-webhook-signing-key-x"
                value={formData.emailCioWebhookSigningKey}
                onChange={(e) => {
                  updateFormData({ emailCioWebhookSigningKey: e.target.value })
                }}
              />
            </label>
            <br />
            <label htmlFor="email-cio-reporting-webhook-signing-key-x">
              Reporting webhook signing key &nbsp;
              <input
                type="text"
                id="email-cio-reporting-webhook-signing-key-x"
                name="email-cio-reporting-webhook-signing-key-x"
                value={formData.emailCioReportingWebhookSigningKey}
                onChange={(e) => {
                  updateFormData({
                    emailCioReportingWebhookSigningKey: e.target.value,
                  })
                }}
              />
            </label>
          </div>
        )}
        {formData.emailDeliveryMethod === EmailDeliveryMethod.Webhook && (
          <div>
            <strong>Email Webhook Configuration</strong>
            <br />
            <label htmlFor="email-delivery-webhook-url-x">
              Webhook URL &nbsp;
              <input
                type="text"
                id="email-delivery-webhook-url-x"
                name="email-delivery-webhook-url-x"
                value={formData.emailDeliveryWebhookUrl}
                onChange={(e) => {
                  updateFormData({ emailDeliveryWebhookUrl: e.target.value })
                }}
              />
            </label>
            <br />
            <label htmlFor="email-delivery-webhook-request-headers-x">
              Request headers (as JSON object) &nbsp;
              <input
                type="text"
                id="email-delivery-webhook-request-headers-x"
                name="email-delivery-webhook-request-headers-x"
                value={formData.emailDeliveryWebhookRequestHeaders}
                onChange={(e) => {
                  updateFormData({
                    emailDeliveryWebhookRequestHeaders: e.target.value,
                  })
                }}
              />
            </label>
            <br />
            <label htmlFor="email-delivery-webhook-timeout-x">
              Webhook timeout (integer) &nbsp;
              <input
                type="number"
                id="email-delivery-webhook-timeout-x"
                name="email-delivery-webhook-timeout-x"
                value={formData.emailDeliveryWebhookTimeout}
                onChange={(e) => {
                  updateFormData({
                    emailDeliveryWebhookTimeout: parseInt(e.target.value, 10),
                  })
                }}
              />
            </label>
          </div>
        )}
        <hr />
        <button type="submit" disabled={loading}>
          {loading ? 'Updating...' : 'Update Tenant'}
        </button>
      </form>
    </div>
  )
}
